import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppThunkDispatch } from 'state/store';
import debounce from 'lodash/debounce';
import intl from 'react-intl-universal';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { TableOperationHeader, Tooltip } from '@getsynapse/design-system';
import {
  RequestFilters,
  RequestFiltersKey,
  AppliedFilter,
} from 'utils/types/filters';
import FilterButton from 'Organisms/TableHeaderActionButtons/FilterButton';
import AppliedFiltersTags from 'Pages/ConsolidatedRequestsListPage/components/RequestsSidePanelFilters/RequestsAppliedFiltersTags';
import { REQUEST_TABLE_FILTERS, SLICE_STATUS } from 'utils/constants';
import { REQUEST_STATUSES } from 'utils/constants/request';
import { isDateRangeFilter } from 'utils/typeGuards';
import MoreActionsPopup from 'Organisms/TableHeaderActionButtons/MoreActionsPopup';
import {
  exportRequests,
  exportRequestsByForm,
  selectExportRequestsStatus,
  selectExportRequestsByFormStatus,
} from 'state/ExportRequests/ExportRequestsSlice';
import {
  requestsSelectors,
  getApiErrorMessage,
} from 'state/normalized/requests/requestsSlice';
import ExportIcon from 'assets/icons/export.svg';
import { showNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';

interface RequestsTableOperationHeaderProps {
  onSearchChange: (value: string) => void;
  showFilters: boolean;
  toggleFilters: () => void;
  appliedFilters: RequestFilters;
  handleUpdateFilters: (filters: RequestFilters) => void;
  showQuickFilters?: boolean;
  showMyRequestsFilterButton?: boolean;
  canExportRequests?: boolean;
  selectedRowsIds?: string[];
}

type RequestFilterKey =
  typeof REQUEST_TABLE_FILTERS[keyof typeof REQUEST_TABLE_FILTERS];

const RequestsTableOperationHeader = ({
  onSearchChange,
  showFilters,
  toggleFilters,
  appliedFilters,
  handleUpdateFilters,
  showQuickFilters = false,
  showMyRequestsFilterButton = false,
  canExportRequests = false,
  selectedRowsIds,
}: RequestsTableOperationHeaderProps) => {
  const dispatch = useAppThunkDispatch();

  const [searchTerm, setSearchTerm] = useState<string>('');

  const exportRequestsStatus = useSelector(selectExportRequestsStatus);
  const exportRequestsByFormStatus = useSelector(
    selectExportRequestsByFormStatus
  );
  const requestsById = useSelector(requestsSelectors.selectEntities);
  const hasLegacyRequestSelected = useMemo(
    () =>
      (selectedRowsIds ?? []).some(
        (id) => requestsById[id]?.isJsonSchema !== true
      ),
    [selectedRowsIds, requestsById]
  );
  const isLegacyExportEnabled = canExportRequests && hasLegacyRequestSelected;

  const {
    intakeActiveTabFilters = false,
    consolidatedRequestsPage,
    advancedRequestsCompletedStatus = false,
  } = useFlags();

  // CGP-183: Completed is a terminal status and cannot coexist with the Open
  // Requests quick filter (which shows only active statuses). They are mutually
  // exclusive: when Completed is in the status filter the Open Requests button
  // is disabled (Scenario 2), and it becomes enabled again once Completed is
  // deselected (Scenario 3). The mirror direction — Completed disabled in the
  // status dropdown while Open Requests is applied — lives in RequestsFiltersForm.
  const isCompletedSelected =
    advancedRequestsCompletedStatus &&
    Array.isArray(appliedFilters[REQUEST_TABLE_FILTERS.STATUS]) &&
    (appliedFilters[REQUEST_TABLE_FILTERS.STATUS] as string[]).includes(
      REQUEST_STATUSES.COMPLETED
    );

  const handleQuickFilterButtonClick = (key: RequestFilterKey) => {
    if (!!appliedFilters[key]) {
      handleRemoveFilter({
        key,
        value: true,
        label: '',
      });
    } else {
      handleUpdateFilters({
        ...appliedFilters,
        [key]: true,
      });
    }
  };

  const quickFiltersButtons = showQuickFilters && [
    ...(showMyRequestsFilterButton
      ? [
          {
            children: intl.get('REQUESTS_LIST_PAGE.SUB_NAV.MY_REQUESTS'),
            onClick: () =>
              handleQuickFilterButtonClick(REQUEST_TABLE_FILTERS.MY_REQUESTS),
            isActive: !!appliedFilters[REQUEST_TABLE_FILTERS.MY_REQUESTS],
          },
        ]
      : []),
    {
      // CGP-183: when Completed is selected, Open Requests is disabled with an
      // explanatory tooltip. The button is not natively `disabled` (a disabled
      // element swallows hover events, so its tooltip would never show) — it is
      // styled as disabled, made non-interactive, and its label is wrapped in a
      // Tooltip that still fires on hover.
      children: isCompletedSelected ? (
        <Tooltip
          openMode='hover1'
          timeout={0}
          showPopper
          usePortal
          position='bottomCenter'
          contentProps={{ className: 'z-200000 px-3 py-2 text-sm' }}
          trigger={
            <span>
              {intl.get('REQUESTS_LIST_PAGE.SUB_NAV.ACTIVE_REQUESTS')}
            </span>
          }
        >
          {intl.get('REQUESTS_LIST_PAGE.SUB_NAV.OPEN_EXCLUDES_TERMINAL')}
        </Tooltip>
      ) : (
        intl.get('REQUESTS_LIST_PAGE.SUB_NAV.ACTIVE_REQUESTS')
      ),
      onClick: isCompletedSelected
        ? undefined
        : () =>
            handleQuickFilterButtonClick(REQUEST_TABLE_FILTERS.OPEN_REQUESTS),
      isActive: !!appliedFilters[REQUEST_TABLE_FILTERS.OPEN_REQUESTS],
      'aria-disabled': isCompletedSelected || undefined,
      tabIndex: isCompletedSelected ? -1 : undefined,
      className: isCompletedSelected
        ? 'text-neutral bg-neutral-lightest-two border-primary-lighter-two cursor-not-allowed hover:bg-neutral-lightest-two hover:shadow-none'
        : undefined,
    },
  ];

  const debouncedSearchChange = useMemo(() => {
    return debounce((value: string) => {
      onSearchChange(value);
    }, 500);
  }, [onSearchChange]);

  const onExportHandler = async (callback: () => void) => {
    try {
      // CGP-221: .unwrap() so a rejected export (e.g. 400 "There are no
      // requests to export" when select-all filters match nothing) actually
      // reaches the catch — awaiting the bare dispatch never throws.
      await dispatch(exportRequests(selectedRowsIds!)).unwrap();
    } catch (error) {
      // Surface the backend's reason (e.g. 400 "There are no requests to
      // export") forwarded by the thunk's rejectWithValue; fall back to the
      // generic message for network/unknown failures.
      dispatch(
        showNotification({
          notificationVariant: 'error',
          notificationTitle: intl.get('REQUESTS_LIST_PAGE.EXPORT_ERROR_TITLE'),
          notificationMessage:
            getApiErrorMessage(error) ??
            intl.get('REQUESTS_LIST_PAGE.EXPORT_ERROR_MESSAGE'),
        })
      );
    } finally {
      callback();
    }
  };

  const onExportByFormHandler = async (callback: () => void) => {
    try {
      await dispatch(exportRequestsByForm(selectedRowsIds!)).unwrap();
    } catch (error) {
      dispatch(
        showNotification({
          notificationVariant: 'error',
          notificationTitle: intl.get('REQUESTS_LIST_PAGE.EXPORT_ERROR_TITLE'),
          notificationMessage:
            getApiErrorMessage(error) ??
            intl.get('REQUESTS_LIST_PAGE.EXPORT_ERROR_MESSAGE'),
        })
      );
    } finally {
      callback();
    }
  };

  const tableActionsList = [
    {
      label: intl.get('REQUESTS_LIST_PAGE.EXPORT'),
      selectOption: onExportHandler,
      disabled: !isLegacyExportEnabled,
      tooltip: {
        text: isLegacyExportEnabled
          ? intl.get('REQUESTS_LIST_PAGE.EXPORT_TOOLTIP')
          : '',
      },
      iconSrc: ExportIcon,
      loading: exportRequestsStatus === SLICE_STATUS.LOADING,
      dataCy: 'export-requests-button',
    },
    {
      label: intl.get('REQUESTS_LIST_PAGE.EXPORT_BY_FORM'),
      selectOption: onExportByFormHandler,
      disabled: !canExportRequests,
      tooltip: {
        text: canExportRequests
          ? intl.get('REQUESTS_LIST_PAGE.EXPORT_TOOLTIP')
          : '',
      },
      iconSrc: ExportIcon,
      loading: exportRequestsByFormStatus === SLICE_STATUS.LOADING,
      dataCy: 'export-requests-by-form-button',
    },
  ];

  const renderActionButtons = () => (
    <div className='flex'>
      {intakeActiveTabFilters && (
        <FilterButton
          aria-pressed={showFilters}
          onToggleFilter={toggleFilters}
          data-testid='openRequests-table__filter-button'
        />
      )}
      {consolidatedRequestsPage && (
        <MoreActionsPopup options={tableActionsList} />
      )}
    </div>
  );

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedSearchChange(value);
  };

  const handleRemoveFilter = (filter: AppliedFilter) => {
    const { key, value } = filter;
    const filterKey = key as RequestFiltersKey;
    const updatedFilters = { ...appliedFilters };
    if (
      isDateRangeFilter(appliedFilters[filterKey]) ||
      typeof value === 'boolean'
    ) {
      delete updatedFilters[filterKey];
    } else {
      const filterValues = updatedFilters[filterKey] as string[];
      const updatedFilterValues = filterValues.filter(
        (filterValue) => filterValue !== value
      );
      if (updatedFilterValues.length === 0) {
        delete updatedFilters[filterKey];
      } else {
        updatedFilters[filterKey] = updatedFilterValues;
      }
    }
    handleUpdateFilters(updatedFilters);
  };

  return (
    <>
      <TableOperationHeader
        searchProps={{
          'data-testid': 'openRequests-search_input',
          placeholder: intl.get('SEARCH_PLACEHOLDER'),
        }}
        inputValue={searchTerm}
        setInputValue={handleSearchChange}
        actionButtons={renderActionButtons()}
        quickFilterButtons={quickFiltersButtons}
      />
      <AppliedFiltersTags
        filters={appliedFilters}
        onDisplayAllFilters={toggleFilters}
        onUpdateFilters={handleUpdateFilters}
        onRemoveFilter={handleRemoveFilter}
      />
    </>
  );
};

export default RequestsTableOperationHeader;
